import React, { useState, useEffect, useRef } from 'react';

const AromaPodProApp = () => {
    // State management
    const [activeScreen, setActiveScreen] = useState('sleep');
    const [activeElement, setActiveElement] = useState(null);
    const [intensity, setIntensity] = useState(20);
    const [isDragging, setIsDragging] = useState(false);
    const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
    const [giftMessage, setGiftMessage] = useState('');
    const [currentTime, setCurrentTime] = useState('');

    const intensityHandleRef = useRef(null);
    const intensityBarRef = useRef(null);

    // Element descriptions
    const elementDescriptions = {
        Metal: "Clarity and precision. Perfect for focus and mindfulness.",
        Wood: "Growth and vitality. Creates harmony with your environment.",
        Fire: "Energy and passion. Revitalizes your creative spirit.",
        Earth: "Grounding and balance. Creates stability and comfort."
    };

    // Update time display
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            let hours = now.getHours();
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            setCurrentTime(`${hours}:${minutes} ${ampm}`);
        };

        updateTime();
        const intervalId = setInterval(updateTime, 60000);

        return () => clearInterval(intervalId);
    }, []);

    // Intensity drag handlers
    const handleIntensityStart = (event) => {
        setIsDragging(true);
        updateIntensityPosition(event);
    };

    const handleIntensityMove = (event) => {
        if (isDragging) {
            updateIntensityPosition(event);
        }
    };

    const handleIntensityEnd = () => {
        setIsDragging(false);
    };

    // Update intensity based on drag
    const updateIntensityPosition = (event) => {
        if (!intensityBarRef.current) return;

        const clientX = event.clientX || (event.touches && event.touches[0].clientX);
        const rect = intensityBarRef.current.getBoundingClientRect();
        let position = ((clientX - rect.left) / rect.width) * 100;
        position = Math.max(0, Math.min(100, position));
        setIntensity(position);
    };

    // Handle start button click
    const handleStart = () => {
        if (!activeElement) {
            alert('Please select an element first to begin your aromatherapy experience.');
            return;
        }

        alert(`Your ${activeElement} aromatherapy experience is beginning at ${Math.round(intensity)}% intensity.`);
    };

    // Handle element selection
    const handleElementSelect = (element) => {
        if (activeElement === element) {
            setActiveElement(null);
        } else {
            setActiveElement(element);
        }
    };

    // Send gift with message
    const handleSendGift = () => {
        alert('Thank you for your gift order! Your personal message has been included.');
        setIsGiftModalOpen(false);
        setGiftMessage('');
    };

    // CSS styles
    const styles = {
        deviceContainer: {
            position: 'relative',
            width: '440px',      // CHANGE FROM 460px to 440px
            height: '280px',     // CHANGE FROM 300px to 280px
            background: '#000',
            overflow: 'hidden',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '5px solid #3c3c3c',   // CHANGE FROM 6px to 5px
            fontFamily: '"Playfair Display", "Times New Roman", serif',
            userSelect: 'none',
        },

        // Screen styles
        screen: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: 0,
            pointerEvents: 'none',
        },

        activeScreen: {
            opacity: 1,
            pointerEvents: 'all',
            zIndex: 10,
        },

        // Sleep screen
        sleepScreen: {
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
        },

        sleepText: {
            color: 'rgba(255, 255, 255, 0.2)',
            fontSize: '18px',
            fontStyle: 'italic',
            marginBottom: '20px',
        },

        touchHint: {
            color: 'rgba(255, 255, 255, 0.1)',
            fontSize: '14px',
            animation: 'pulse 2s infinite',
        },

        // Welcome screen
        welcomeScreen: {
            background: 'linear-gradient(to bottom, #d6cfc7, #e8e4d9)',
            color: '#5a4a42',
            display: 'flex',
            flexDirection: 'column',
        },

        welcomeHeader: {
            height: '50px',
            padding: '0 15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#b7aa9a',
            borderBottom: '1px solid rgba(90, 74, 66, 0.2)',
        },

        hotelBranding: {
            fontSize: '13px',
            fontWeight: 400,
            letterSpacing: '1px',
            color: '#5a4a42',
            display: 'flex',
            alignItems: 'center',
        },

        hotelLogo: {
            width: '25px',
            height: '25px',
            marginRight: '8px',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 7h-8v7H3V7H1v10h22V7h-4z\'/%3E%3Cpath d=\'M10 0H2v5h8V0zm12 0h-8v5h8V0z\'/%3E%3C/svg%3E")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        },

        time: {
            fontSize: '16px',
            color: '#5a4a42',
            fontWeight: 400,
        },

        // Reduce the padding in welcomeContent to make more room for the footer
        welcomeContent: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px', // CHANGE THIS FROM 15px to 10px
            textAlign: 'center',
        },

        // Make the welcome greeting and message smaller
        welcomeGreeting: {
            fontSize: '20px', // CHANGE THIS FROM 22px to 20px
            color: '#5a4a42',
            marginBottom: '8px', // CHANGE THIS FROM 12px to 8px
            fontWeight: 400,
            letterSpacing: '1px',
        },

        welcomeMessage: {
            fontSize: '13px', // CHANGE THIS FROM 14px to 13px
            color: '#7d6b60',
            marginBottom: '15px', // CHANGE THIS FROM 20px to 15px
            lineHeight: 1.4,
            maxWidth: '90%',
        },

        // Make the QR container smaller
        qrContainer: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '15px', // CHANGE THIS FROM 20px to 15px
        },

        welcomeQr: {
            width: '45px', // CHANGE THIS FROM 100px to 90px
            height: '45px', // CHANGE THIS FROM 100px to 90px
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '5px',
            marginRight: '15px',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 33 33\' shape-rendering=\'crispEdges\'%3E%3Cpath fill=\'%23ffffff\' d=\'M0 0h33v33H0z\'/%3E%3Cpath stroke=\'%23000000\' d=\'M4 4.5h7m1 0h1m1 0h2m2 0h1m1 0h1m1 0h7M4 5.5h1m5 0h1m1 0h3m3 0h3m1 0h1m5 0h1M4 6.5h1m1 0h3m1 0h1m2 0h3m1 0h1m1 0h1m1 0h3m1 0h1m1 0h3m1 0h1M4 7.5h1m1 0h3m1 0h1m1 0h1m1 0h1m3 0h1m3 0h1m1 0h1m1 0h3m1 0h1M4 8.5h1m1 0h3m1 0h1m1 0h1m1 0h5m3 0h1m1 0h1m1 0h3m1 0h1M4 9.5h1m5 0h1m1 0h4m1 0h2m2 0h1m1 0h1m5 0h1M4 10.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M12 11.5h1m1 0h3m1 0h2M5 12.5h1m1 0h2m1 0h5m1 0h4m2 0h1m1 0h2m3 0h1M4 13.5h1m1 0h1m3 0h1m4 0h1m1 0h1m1 0h1m1 0h1m1 0h1m4 0h3M4 14.5h1m1 0h5m1 0h1m2 0h2m2 0h4m4 0h1m1 0h2M4 15.5h2m4 0h1m2 0h1m1 0h1m3 0h2m2 0h1m3 0h1m2 0h1M9 16.5h1m3 0h1m1 0h2m1 0h1m2 0h1m1 0h1m5 0h1M4 17.5h7m1 0h1m1 0h1m2 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m2 0h1M4 18.5h1m5 0h1m3 0h1m2 0h2m5 0h3m1 0h1M4 19.5h1m1 0h3m1 0h1m6 0h2m2 0h1m2 0h2m1 0h1m1 0h1M4 20.5h1m1 0h3m1 0h1m1 0h3m3 0h1m2 0h1m1 0h1m1 0h1m1 0h3M4 21.5h1m1 0h3m1 0h1m2 0h1m1 0h2m1 0h2m1 0h1m1 0h4m1 0h1M4 22.5h1m5 0h1m1 0h1m3 0h1m2 0h1m1 0h3m1 0h3M4 23.5h7m1 0h1m3 0h4m1 0h1m1 0h1m3 0h1m1 0h1M12 24.5h3m1 0h1m1 0h4m3 0h1m2 0h1M5 25.5h6m1 0h1m1 0h1m1 0h1m2 0h1m1 0h1m1 0h3m2 0h1M4 26.5h2m2 0h3m2 0h3m5 0h1m2 0h2m1 0h2M5 27.5h1m1 0h1m3 0h1m3 0h1m4 0h1m3 0h2m1 0h1M4 28.5h1m2 0h1m1 0h1m1 0h4m1 0h1m1 0h1m3 0h1m2 0h3M12 29.5h1m1 0h1m1 0h1m5 0h2m4 0h1M4 30.5h7m1 0h3m1 0h1m4 0h1m1 0h1m3 0h1M4 31.5h1m5 0h1m1 0h1m4 0h3m2 0h1m1 0h1m2 0h1M4 32.5h1m1 0h3m1 0h1m2 0h1m1 0h1m1 0h1m1 0h1m1 0h1m2 0h1m1 0h4\'/%3E%3C/svg%3E")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },

        // Make the welcome footer more visible
        welcomeFooter: {
            padding: '10px',
            textAlign: 'center',
            marginTop: 'auto', // ADD THIS to push it to the bottom
            borderTop: '1px solid rgba(90, 74, 66, 0.1)', // ADD THIS for visibility
            backgroundColor: 'rgba(255, 255, 255, 0.1)', // ADD THIS for visibility
        },

        offlineOption: {
            fontSize: '12px',
            color: 'rgba(90, 74, 66, 0.8)', // CHANGE THIS from 0.6 to 0.8 for visibility
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'inline-block',
            padding: '5px 15px', // ADD THIS to make the tap target larger
            borderRadius: '15px', // ADD THIS for visual appeal
            border: '1px solid rgba(90, 74, 66, 0.2)', // ADD THIS for visibility
        },

        // Control screen
        controlScreen: {
            background: 'linear-gradient(to bottom, #d6cfc7, #e8e4d9)',
            color: '#5a4a42',
            display: 'flex',
            flexDirection: 'column',
        },

        controlHeader: {
            height: '50px',
            padding: '0 15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#b7aa9a',
            borderBottom: '1px solid rgba(90, 74, 66, 0.2)',
        },

        backBtn: {
            display: 'flex',
            alignItems: 'center',
            color: '#5a4a42',
            fontSize: '14px',
            cursor: 'pointer',
        },

        backIcon: {
            width: '20px',
            height: '20px',
            marginRight: '5px',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z\'/%3E%3C/svg%3E")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        },

        controlTitle: {
            fontSize: '16px',
            fontWeight: 400,
            letterSpacing: '1px',
        },

        controlMode: {
            fontSize: '14px',
            color: '#7d6b60',
            fontStyle: 'italic',
        },

        controlContent: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
        },

        sectionTitle: {
            textAlign: 'center',
            fontSize: '17px',
            color: '#5a4a42',
            marginBottom: '12px',
            fontWeight: 400,
        },

        elementSelector: {
            display: 'flex',
            justifyContent: 'center',
            padding: '0 10px 0px',
        },

        elementOption: {
            position: 'relative',
            width: '65px',
            margin: '0 8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
        },

        elementCircle: (isActive, type) => ({
            width: '45px',
            height: '45px',
            borderRadius: '50%',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(90, 74, 66, 0.3)',
            backgroundColor: isActive ?
                type === 'Metal' ? '#b5a898' :
                    type === 'Wood' ? '#a08a6a' :
                        type === 'Fire' ? '#a57e76' : '#868778'
                :
                type === 'Metal' ? '#d1c7b7' :
                    type === 'Wood' ? '#bfa887' :
                        type === 'Fire' ? '#c1a099' : '#a5a897',
            boxShadow: isActive ? `0 0 15px rgba(${type === 'Metal' ? '181, 168, 152' :
                type === 'Wood' ? '160, 138, 106' :
                    type === 'Fire' ? '165, 126, 118' : '134, 135, 120'
                }, 0.5)` : 'none',
        }),

        elementName: {
            fontSize: '13px',
            color: '#5a4a42',
            textAlign: 'center',
            fontWeight: 400,
        },

        elementIcon: {
            width: '22px',
            height: '22px',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.7,
            transition: 'opacity 0.3s ease',
        },

        metalIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M12 6c1.11 0 2-.9 2-2s-.89-2-2-2c-1.1 0-2 .9-2 2s.9 2 2 2zm4.84 1.5c-.28-.7-1.12-1.5-1.84-1.5h-2v-1h2c.55 0 1-.45 1-1s-.45-1-1-1h-2v-1c0-.55-.45-1-1-1s-1 .45-1 1v1h-2c-.55 0-1 .45-1 1s.45 1 1 1h2v1h-2c-.72 0-1.56.8-1.84 1.5-1.21 3.04-4.16 5.5-4.16 5.5v10h18V13s-2.95-2.46-4.16-5.5zM6.5 18.5c0-2.67 1-5 1-5s2.33 1.67 4.5 1.67 4.5-1.67 4.5-1.67 1 2.33 1 5h-11z\'/%3E%3C/svg%3E")',
        },

        woodIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z\'/%3E%3C/svg%3E")',
        },

        fireIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z\'/%3E%3C/svg%3E")',
        },

        earthIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z\'/%3E%3C/svg%3E")',
        },

        intensityArea: {
            marginTop: '5px',
            padding: '0 20px',
        },

        intensityLabel: {
            textAlign: 'center',
            fontSize: '15px',
            color: '#5a4a42',
            marginBottom: '8px',
            fontWeight: 400,
        },

        intensitySliderContainer: {
            position: 'relative',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
        },

        intensityBar: {
            position: 'relative',
            height: '6px',
            background: 'linear-gradient(to right, #d6cfc7, #a08a6a, #5a4a42)',
            width: '100%',
            borderRadius: '3px',
        },

        intensityHandle: {
            position: 'absolute',
            width: '22px',
            height: '22px',
            backgroundColor: '#fff',
            border: '2px solid #5a4a42',
            borderRadius: '50%',
            top: '50%',
            left: `${intensity}%`,
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            transition: 'transform 0.1s ease',
        },

        intensityMarkers: {
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 8px',
            marginTop: '5px',
        },

        intensityMarker: {
            fontSize: '11px',
            color: '#5a4a42',
            opacity: 0.7,
        },

        controlOptions: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
            gap: '15px',
        },

        controlOption: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '70px',
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
        },

        controlBtn: (isStart) => ({
            width: '45px',
            height: '45px',
            borderRadius: '50%',
            backgroundColor: isStart ? '#5a4a42' : 'rgba(90, 74, 66, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '6px',
            transition: 'all 0.3s ease',
            border: isStart ? 'none' : '1px solid rgba(90, 74, 66, 0.2)',
        }),

        controlIcon: {
            width: '22px',
            height: '22px',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        },

        startIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%23e8e4d9\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M8 5v14l11-7z\'/%3E%3C/svg%3E")',
        },

        scheduleIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z\'/%3E%3C/svg%3E")',
        },

        giftIcon: {
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'%235a4a42\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z\'/%3E%3C/svg%3E")',
        },

        controlLabel: {
            fontSize: '11px',
            color: '#5a4a42',
            textAlign: 'center',
        },

        // Modal
        modal: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(90, 74, 66, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: isGiftModalOpen ? 1 : 0,
            pointerEvents: isGiftModalOpen ? 'all' : 'none',
            transition: 'opacity 0.5s ease',
            zIndex: 100,
        },

        modalContent: {
            backgroundColor: '#e8e4d9',
            borderRadius: '10px',
            padding: '20px',
            maxWidth: '85%',
            textAlign: 'center',
            position: 'relative',
        },

        modalTitle: {
            fontSize: '17px',
            color: '#5a4a42',
            marginBottom: '8px',
            fontWeight: 400,
        },

        modalText: {
            fontSize: '13px',
            color: '#5a4a42',
            marginBottom: '15px',
            lineHeight: 1.5,
        },

        giftNote: {
            fontSize: '12px',
            fontStyle: 'italic',
            color: '#7d6b60',
            marginTop: '6px',
            marginBottom: '10px',
        },

        giftInput: {
            width: '90%',
            margin: '0 auto 12px',
            padding: '8px 10px',
            border: '1px solid #b7aa9a',
            borderRadius: '4px',
            backgroundColor: '#f3f0ea',
            color: '#5a4a42',
            fontFamily: '"Playfair Display", "Times New Roman", serif',
            fontSize: '13px',
        },

        modalButtons: {
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
        },

        modalBtn: (isCancel) => ({
            padding: '7px 18px',
            backgroundColor: isCancel ? 'transparent' : '#5a4a42',
            color: isCancel ? '#5a4a42' : '#e8e4d9',
            border: isCancel ? '1px solid #5a4a42' : 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '13px',
            fontFamily: '"Playfair Display", "Times New Roman", serif',
            transition: 'all 0.3s ease',
        }),
    };

    return (
        <div style={styles.deviceContainer}>
            {/* Sleep Screen */}
            <div
                style={{ ...styles.screen, ...styles.sleepScreen, ...(activeScreen === 'sleep' ? styles.activeScreen : {}) }}
                onClick={() => setActiveScreen('welcome')}
            >
                <div style={styles.sleepText}>AromaPod Pro</div>
                <div style={styles.touchHint}>Touch to wake</div>
            </div>

            {/* Welcome Screen */}
            <div style={{ ...styles.screen, ...styles.welcomeScreen, ...(activeScreen === 'welcome' ? styles.activeScreen : {}) }}>
                <div style={styles.welcomeHeader}>
                    <div style={styles.hotelBranding}>
                        <div style={styles.hotelLogo}></div>
                        <span>GRAND HARMONY HOTEL</span>
                    </div>
                    <div style={styles.time}>{currentTime}</div>
                </div>

                <div style={styles.welcomeContent}>
                    <div style={styles.welcomeGreeting}>Welcome, Esteemed Guest</div>
                    <div style={styles.welcomeMessage}>Experience the luxurious aromatherapy specially curated for your relaxation and wellness during your stay.</div>

                    <div style={styles.qrContainer}>
                        <div style={styles.welcomeQr}></div>
                        <div style={styles.qrText}>
                            <div style={styles.qrTitle}>Cloud Control</div>
                            <div style={styles.qrInstructions}>Scan this QR code with your phone to control your AromaPod Pro through our exclusive chatbot interface.</div>
                        </div>
                    </div>
                </div>

                <div style={styles.welcomeFooter}>
                    <div
                        style={styles.offlineOption}
                        onClick={() => setActiveScreen('control')}
                    >
                        Prefer to use device controls? Tap here
                    </div>
                </div>
            </div>

            {/* Device Control Screen */}
            <div style={{ ...styles.screen, ...styles.controlScreen, ...(activeScreen === 'control' ? styles.activeScreen : {}) }}>
                <div style={styles.controlHeader}>
                    <div style={styles.backBtn} onClick={() => setActiveScreen('welcome')}>
                        <div style={styles.backIcon}></div>
                        <span>Back</span>
                    </div>
                    <div style={styles.controlTitle}>AromaPod Pro</div>
                    <div style={styles.controlMode}>Local Control</div>
                </div>

                <div style={styles.controlContent}>
                    <div style={styles.sectionTitle}>Select Your Aromatherapy Element</div>

                    <div style={styles.elementSelector}>
                        {['Metal', 'Wood', 'Fire', 'Earth'].map(element => (
                            <div
                                key={element}
                                style={styles.elementOption}
                                onClick={() => handleElementSelect(element)}
                            >
                                <div style={styles.elementCircle(activeElement === element, element)}>
                                    <div style={{
                                        ...styles.elementIcon,
                                        ...(element === 'Metal' ? styles.metalIcon :
                                            element === 'Wood' ? styles.woodIcon :
                                                element === 'Fire' ? styles.fireIcon : styles.earthIcon),
                                        opacity: activeElement === element ? 1 : 0.7
                                    }}></div>
                                </div>
                                <div style={styles.elementName}>{element}</div>
                            </div>
                        ))}
                    </div>

                    <div style={styles.intensityArea}>
                        {/* <div style={styles.intensityLabel}>Aroma Intensity</div> */}
                        <div style={styles.intensitySliderContainer}>
                            <div
                                style={styles.intensityBar}
                                ref={intensityBarRef}
                            ></div>
                            <div
                                style={styles.intensityHandle}
                                ref={intensityHandleRef}
                                onMouseDown={handleIntensityStart}
                                onTouchStart={handleIntensityStart}
                            ></div>
                            <div style={styles.intensityMarkers}>
                                <div style={styles.intensityMarker}>Subtle</div>
                                <div style={styles.intensityMarker}>Soft</div>
                                <div style={styles.intensityMarker}>Balanced</div>
                                <div style={styles.intensityMarker}>Vibrant</div>
                                <div style={styles.intensityMarker}>Intense</div>
                            </div>
                        </div>
                    </div>

                    <div style={styles.controlOptions}>
                        <div
                            style={styles.controlOption}
                            onClick={handleStart}
                        >
                            <div style={styles.controlBtn(true)}>
                                <div style={{ ...styles.controlIcon, ...styles.startIcon }}></div>
                            </div>
                            <div style={styles.controlLabel}>Trial</div>
                        </div>
                        <div style={styles.controlOption}>
                            <div style={styles.controlBtn(false)}>
                                <div style={{ ...styles.controlIcon, ...styles.scheduleIcon }}></div>
                            </div>
                            <div style={styles.controlLabel}>Schedule</div>
                        </div>
                        {/* <div
                            style={styles.controlOption}
                            onClick={() => setIsGiftModalOpen(true)}
                        >
                            <div style={styles.controlBtn(false)}>
                                <div style={{ ...styles.controlIcon, ...styles.giftIcon }}></div>
                            </div>
                            <div style={styles.controlLabel}>Gift</div>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Gift Modal */}
            <div style={styles.modal}>
                <div style={styles.modalContent}>
                    <div style={styles.modalTitle}>Share the Luxury Experience</div>
                    <div style={styles.modalText}>Send the AromaPod Pro as a thoughtful gift. Scan the QR code to complete your order with a 15% discount for hotel guests.</div>
                    <div style={styles.giftNote}>Add a personal message to your gift</div>
                    <input
                        type="text"
                        style={styles.giftInput}
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        placeholder="Enter your personal message here..."
                    />
                    <div style={styles.modalButtons}>
                        <button
                            style={styles.modalBtn(true)}
                            onClick={() => setIsGiftModalOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            style={styles.modalBtn(false)}
                            onClick={handleSendGift}
                        >
                            Send Gift
                        </button>
                    </div>
                </div>
            </div>

            {/* Mouse move event listeners for intensity slider */}
            {isDragging && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            zIndex: 1000,
                        }}
                        onMouseMove={handleIntensityMove}
                        onMouseUp={handleIntensityEnd}
                        onTouchMove={handleIntensityMove}
                        onTouchEnd={handleIntensityEnd}
                    />
                </>
            )}
        </div>
    );
};

export default AromaPodProApp;